import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
import ExportJsonButton from '../../components/admin/ExportJsonButton';
import { useSecurityOverview } from '../../hooks/admin/useAdminSecurity';
import { useAdminAlertsBadge } from '../../hooks/admin/useAdminAlerts';
import { collectSecuritySnapshot, parseSecurityHours, securityExportFilename } from '../../lib/adminSecurityExport';
import OverviewTab from './security/OverviewTab';
import EventsTab from './security/EventsTab';
import AuthEventsTab from './security/AuthEventsTab';
import BlockedIpsTab from './security/BlockedIpsTab';
import AlertsTab from './security/AlertsTab';
import { isSecurityTab, type SecurityTab } from './security/tabs';

export default function AdminSecurity() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const tab: SecurityTab = isSecurityTab(rawTab) ? rawTab : 'overview';
  const hours = parseSecurityHours(searchParams.get('hours'));
  const { count: unacked } = useAdminAlertsBadge();
  // selfIp comes from the overview; cached so the blocked-IPs tab can warn too.
  const overview = useSecurityOverview(hours);

  const setTab = useCallback(
    (value: string) => {
      // Each tab owns its own filter keys (page, ip, type…), so switching tabs
      // starts from a clean query string — except the overview window, which the
      // all-tabs download still needs.
      const next = new URLSearchParams();
      if (value !== 'overview') next.set('tab', value);
      const hoursKept = searchParams.get('hours');
      if (hoursKept) next.set('hours', hoursKept);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  return (
    <div className="space-y-6 text-glass-text">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="page-title">Security</h1>
          <p className="mt-1 text-sm text-glass-text-2">
            Traffic, abuse signals, sign-in activity, IP blocks and alert rules for the whole platform.
          </p>
        </div>
        <ExportJsonButton
          label="Download all tabs"
          filename={() => securityExportFilename('all', hours)}
          collect={() => collectSecuritySnapshot(hours)}
        />
      </div>

      <Tabs defaultValue="overview" value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="events">Events</TabsTrigger>
          <TabsTrigger value="auth">Sign-ins</TabsTrigger>
          <TabsTrigger value="blocked">Blocked IPs</TabsTrigger>
          <TabsTrigger value="alerts">
            Alerts
            {unacked > 0 && (
              <span className="ml-2 rounded-full bg-[rgb(var(--warn)/0.2)] px-1.5 py-0.5 text-[0.65rem] font-bold text-[rgb(var(--warn))]">
                {unacked}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <OverviewTab onNavigateTab={setTab} />
        </TabsContent>
        <TabsContent value="events" className="mt-4">
          <EventsTab />
        </TabsContent>
        <TabsContent value="auth" className="mt-4">
          <AuthEventsTab />
        </TabsContent>
        <TabsContent value="blocked" className="mt-4">
          <BlockedIpsTab selfIp={overview.data?.selfIp ?? null} />
        </TabsContent>
        <TabsContent value="alerts" className="mt-4">
          <AlertsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
